import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CornerDownLeft, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import { isDisposableEmail } from "@/lib/email";

// Sign-up, one question at a time:
//   email → "What should we call you?" → "Hi Om, what's your company?"
//   → password → the 6-digit code from the email.
//
// Every answer is saved the moment the person moves on (capture_signup_lead,
// migration 031), so someone who stops after the email is still captured.
// The answers also stay in this tab's sessionStorage, so a refresh doesn't
// lose them.

const DRAFT_KEY = "il_signup_draft";
const STEPS = ["email", "name", "company", "password", "verify"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const newLeadId = () =>
  (window.crypto?.randomUUID?.() ||
    "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));

function loadDraft() {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null") || {}; } catch { return {}; }
}

export default function Register() {
  const { user, register, verifyEmailOtp, resendSignupOtp, loginWithGoogle, error, setError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const draft = useMemo(loadDraft, []);
  const [leadId] = useState(() => draft.leadId || newLeadId());
  const [step, setStep] = useState(draft.step && draft.step !== "verify" ? draft.step : "email");
  const [email, setEmail] = useState(draft.email || params.get("email") || "");
  const [name, setName] = useState(draft.name || "");
  const [company, setCompany] = useState(draft.company || "");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const inputRef = useRef(null);
  // Set while the account is being created or verified, so the "already
  // signed in" redirect below doesn't race the hand-off to /welcome.
  const completing = useRef(false);

  // Where this sign-up came from: the website (with ?plan=&offer=&utm_*) or
  // the Chrome extension (?source=extension).
  const meta = useMemo(() => {
    const utm = {};
    for (const [k, v] of params.entries()) if (k.startsWith("utm_")) utm[k] = v.slice(0, 100);
    return {
      source: (params.get("source") || "website").slice(0, 40),
      plan: params.get("plan") || undefined,
      offer: params.get("offer") || undefined,
      utm,
    };
  }, [params]);

  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ leadId, step, email, name, company }));
    } catch { /* noop */ }
  }, [leadId, step, email, name, company]);

  useEffect(() => { inputRef.current?.focus(); }, [step]);

  if (user && user !== false && !completing.current) return <Navigate to="/research" replace />;

  const firstName = name.trim().split(/\s+/)[0] || "";

  // Fire-and-forget: capturing a lead must never slow down or block sign-up.
  const capture = (nextStep, fields) => {
    supabase.rpc("capture_signup_lead", {
      p_lead_id: leadId, p_step: nextStep, p_meta: meta,
      p_email: fields.email ?? null, p_name: fields.name ?? null, p_company: fields.company ?? null,
    }).then(() => {}, () => {});
  };

  const go = (next) => { setError(""); setStep(next); };
  const back = () => go(STEPS[Math.max(0, STEPS.indexOf(step) - 1)]);

  const welcomeUrl = () => {
    const q = new URLSearchParams();
    if (meta.source !== "website") q.set("source", meta.source);
    if (meta.plan) q.set("plan", meta.plan);
    return `/welcome${q.toString() ? `?${q}` : ""}`;
  };
  const finished = () => {
    try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* noop */ }
    navigate(welcomeUrl());
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (step === "email") {
      const v = email.trim();
      if (!EMAIL_RE.test(v)) { setError("Enter your full email address, like you@company.com."); return; }
      if (isDisposableEmail(v)) {
        setError("Please use a permanent email address — temporary inboxes aren't allowed.");
        return;
      }
      capture("name", { email: v });
      go("name");
    } else if (step === "name") {
      if (!name.trim()) { setError("Tell us what to call you."); return; }
      capture("company", { name: name.trim() });
      go("company");
    } else if (step === "company") {
      if (!company.trim()) { setError("Add your company name, or your own name if you work for yourself."); return; }
      capture("password", { company: company.trim() });
      go("password");
    } else if (step === "password") {
      if (password.length < 6) { setError("Use at least 6 characters."); return; }
      setBusy(true);
      completing.current = true;
      const res = await register(name.trim(), email.trim(), password, {
        company: company.trim(), signup_source: meta.source, lead_id: leadId,
      });
      setBusy(false);
      if (!res.ok) { completing.current = false; return; }
      capture("verify", {});
      if (res.needsVerification) { completing.current = false; go("verify"); }
      else finished();
    } else if (step === "verify") {
      setBusy(true);
      completing.current = true;
      const ok = await verifyEmailOtp(email.trim(), otp.trim());
      setBusy(false);
      if (ok) finished();
      else completing.current = false;
    }
  };

  const onResend = async () => {
    setResent(false);
    const ok = await resendSignupOtp(email.trim());
    if (ok) setResent(true);
  };

  const onGoogle = async () => {
    setGoogleLoading(true);
    const ok = await loginWithGoogle();
    if (!ok) setGoogleLoading(false); // on success the browser redirects away
  };

  const progress = ((STEPS.indexOf(step) + 1) / STEPS.length) * 100;

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background text-foreground">
      <div aria-hidden="true" className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[760px] -translate-x-1/2 rounded-full"
        style={{ background: "radial-gradient(closest-side, hsl(var(--primary) / 0.14), transparent)" }} />

      <div className="relative h-1 w-full bg-border" aria-hidden="true">
        <div className="h-full bg-primary transition-[width] duration-300 ease-out" style={{ width: `${progress}%` }} />
      </div>

      <header className="relative flex items-center justify-between gap-4 px-6 py-5 md:px-10">
        <Link to="/" aria-label="Inbound Labs home"><Logo size={28} textClass="text-lg" /></Link>
        <p className="text-sm text-muted-foreground">
          Have an account?{" "}
          <Link to="/login" className="font-medium text-foreground underline underline-offset-4" data-testid="goto-login-link">Sign in</Link>
        </p>
      </header>

      <main className="relative flex flex-1 items-center justify-center px-6 pb-20">
        <form onSubmit={submit} data-testid={step === "verify" ? "verify-form" : "register-form"} data-step={step}
          className="w-full max-w-xl" key={step} noValidate>
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
            <div className="flex items-center gap-2 text-sm font-medium text-violet-text">
              <span className="tabular-nums">{STEPS.indexOf(step) + 1}</span>
              <ArrowRight className="h-3.5 w-3.5" />
              <span className="text-muted-foreground">
                {step === "email" ? "Start with 100 free credits, no card needed"
                  : step === "verify" ? "Last step" : `Step ${STEPS.indexOf(step) + 1} of ${STEPS.length}`}
              </span>
            </div>

            {step === "email" && (
              <Question title="What's your work email?" hint="We'll send a 6-digit code to confirm it.">
                <Input ref={inputRef} id="email" type="email" autoComplete="email" value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  placeholder="you@company.com" className="h-14 text-lg" data-testid="signup-email-input" aria-label="Work email" />
              </Question>
            )}
            {step === "name" && (
              <Question title="What should we call you?">
                <Input ref={inputRef} id="name" autoComplete="name" value={name}
                  onChange={(e) => { setName(e.target.value); setError(""); }}
                  placeholder="Your name" className="h-14 text-lg" data-testid="signup-name-input" aria-label="Your name" />
              </Question>
            )}
            {step === "company" && (
              <Question title={<>Hi {firstName || "there"}, what's your company?</>} hint="So we can set up your workspace.">
                <Input ref={inputRef} id="company" autoComplete="organization" value={company}
                  onChange={(e) => { setCompany(e.target.value); setError(""); }}
                  placeholder="Company name" className="h-14 text-lg" data-testid="signup-company-input" aria-label="Company" />
              </Question>
            )}
            {step === "password" && (
              <Question title="Create a password" hint={`You'll sign in with ${email.trim()} and this password.`}>
                <Input ref={inputRef} id="password" type="password" autoComplete="new-password" value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  placeholder="At least 6 characters" className="h-14 text-lg" data-testid="signup-password-input" aria-label="Password" />
              </Question>
            )}
            {step === "verify" && (
              <Question title="Check your inbox" hint={<>We sent a 6-digit code to <span className="text-foreground">{email.trim()}</span>.</>}>
                <Input ref={inputRef} id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={8} value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 8)); setError(""); }}
                  placeholder="••••••" className="h-14 text-center text-2xl tracking-[0.5em]" data-testid="verify-otp-input" aria-label="Verification code" />
              </Question>
            )}

            {error && (
              <div data-testid={step === "verify" ? "verify-error" : "register-error"}
                className="rounded-lg border border-destructive/40 bg-danger-soft px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" size="lg" className="gap-2"
                disabled={busy || (step === "verify" && otp.length < 6)}
                data-testid={step === "verify" ? "verify-submit-button" : "signup-next-button"}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {step === "password" ? "Create account" : step === "verify" ? "Verify & continue" : "Continue"}
              </Button>
              {step !== "verify" && (
                <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex">
                  or press Enter <CornerDownLeft className="h-3 w-3" />
                </span>
              )}
              {step !== "email" && step !== "verify" && (
                <Button type="button" variant="ghost" className="ml-auto gap-1.5" onClick={back} data-testid="signup-back-button">
                  <ArrowLeft className="h-4 w-4" /> Back
                </Button>
              )}
            </div>

            {step === "email" && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">or</span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <Button type="button" variant="outline" onClick={onGoogle} disabled={googleLoading}
                  data-testid="register-google-button" className="w-full gap-2">
                  {googleLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <GoogleIcon />}
                  {googleLoading ? "Redirecting…" : "Continue with Google"}
                </Button>
              </div>
            )}

            {step === "verify" && (
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  Didn't get it?{" "}
                  <button type="button" onClick={onResend} data-testid="verify-resend-button" className="text-foreground underline underline-offset-4">
                    {resent ? "Code resent" : "Resend code"}
                  </button>
                </p>
                <p>
                  Wrong email?{" "}
                  <button type="button" onClick={() => { setOtp(""); setResent(false); go("email"); }} className="text-foreground underline underline-offset-4">
                    Go back
                  </button>
                </p>
              </div>
            )}
          </div>
        </form>
      </main>
    </div>
  );
}

function Question({ title, hint, children }) {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-[34px] leading-[1.15] tracking-[-0.03em] md:text-[40px]">{title}</h1>
        {hint && <p className="mt-2 text-[15px] text-ink-secondary">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09Z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
      <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
    </svg>
  );
}
